import type { Skill } from "@/features/skills/api/skills-api";
import { KEY_ARROW_DOWN, KEY_ARROW_UP, KEY_ENTER, KEY_ESCAPE } from "@/lib/keyboard-keys";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { RefObject } from "react";

interface UseSlashCommandOptions {
  inputValue: string;
  allSkills: Skill[];
  inputRef: RefObject<HTMLTextAreaElement | null>;
  onInputValueChange: (value: string) => void;
}

interface TriggerState {
  /** Character index of the "/" in the input value. */
  index: number;
  /** Text typed after "/" up to the cursor. */
  query: string;
}

/**
 * Detects a slash-command trigger at the current cursor position.
 *
 * Mirrors diceui's onMentionUpdate logic for "@": the "/" must appear at
 * position 0 or immediately after a space/newline, the text between "/" and
 * the cursor must contain no spaces, and the character immediately after the
 * cursor must be end-of-string or a space.
 *
 * This cursor-aware approach lets "/" work anywhere in a message — including
 * after an @mention — rather than only when it is the very first character.
 */
function detectSlashTrigger(value: string, cursorPos: number): TriggerState | null {
  const textBeforeCursor = value.slice(0, cursorPos);
  const lastSlash = textBeforeCursor.lastIndexOf("/");
  if (lastSlash === -1) return null;

  // "/" must be at the start or preceded by whitespace (word boundary)
  if (lastSlash > 0) {
    const charBefore = value[lastSlash - 1];
    if (charBefore !== " " && charBefore !== "\n") return null;
  }

  // No spaces allowed between "/" and cursor (still in the command word)
  const query = textBeforeCursor.slice(lastSlash + 1);
  if (query.includes(" ")) return null;

  // Cursor must be at word end (end-of-string or followed by whitespace)
  const charAfterCursor = value[cursorPos];
  if (charAfterCursor && charAfterCursor !== " " && charAfterCursor !== "\n") {
    return null;
  }

  return { index: lastSlash, query };
}

/**
 * Manages the "/" slash-command dropdown for the chat input.
 *
 * Uses cursor-aware trigger detection (same approach as diceui's "@" mention)
 * so the command can appear anywhere in the message, not just at the start.
 * On selection it mirrors diceui's onMentionAdd pattern: mutates the DOM
 * value directly, then syncs React state via onInputValueChange.
 */
export function useSlashCommand({
  inputValue: _inputValue,
  allSkills,
  inputRef,
  onInputValueChange,
}: UseSlashCommandOptions) {
  const [dismissed, setDismissed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [triggerState, setTriggerState] = useState<TriggerState | null>(null);

  const open = !dismissed && triggerState !== null;
  const query = triggerState?.query ?? "";

  const filteredSkills = useMemo(() => {
    if (!open) return [];
    if (!query) return allSkills;
    const q = query.toLowerCase();
    return allSkills.filter((s) => s.slug.includes(q) || s.name.toLowerCase().includes(q));
  }, [allSkills, open, query]);

  // Reset highlighted item whenever the query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  /**
   * Called when the user picks a skill (click or Enter).
   * Re-detects the trigger from current DOM state to avoid stale-closure
   * issues, then splices the selected slug into the input value.
   */
  const handleSelect = useCallback(
    (skill: Skill) => {
      const input = inputRef.current;
      if (!input) return;

      const cursorPos = input.selectionStart ?? input.value.length;
      const detected = detectSlashTrigger(input.value, cursorPos);
      if (!detected) return;

      const beforeSlash = input.value.slice(0, detected.index);
      const afterCursor = input.value.slice(cursorPos);
      const newValue = `${beforeSlash}/${skill.slug} ${afterCursor}`;

      // Mirror diceui's onMentionAdd: mutate DOM first, then sync React state
      input.value = newValue;
      const newCursorPos = detected.index + skill.slug.length + 2;
      input.setSelectionRange(newCursorPos, newCursorPos);
      input.focus();

      onInputValueChange(newValue);
      setTriggerState(null);
    },
    [inputRef, onInputValueChange],
  );

  /**
   * Wraps the parent's onChange handler.
   * Reads the cursor position directly from the DOM (accurate at event time)
   * and re-evaluates the trigger state on every keystroke.
   */
  const handleInputChange = useCallback(
    (value: string) => {
      setDismissed(false);
      onInputValueChange(value);
      const cursorPos = inputRef.current?.selectionStart ?? value.length;
      setTriggerState(detectSlashTrigger(value, cursorPos));
    },
    [onInputValueChange, inputRef],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!open) return;
      if (e.key === KEY_ARROW_DOWN) {
        e.preventDefault();
        setSelectedIndex((i) => (filteredSkills.length > 0 ? (i + 1) % filteredSkills.length : 0));
      } else if (e.key === KEY_ARROW_UP) {
        e.preventDefault();
        setSelectedIndex((i) =>
          filteredSkills.length > 0 ? (i - 1 + filteredSkills.length) % filteredSkills.length : 0,
        );
      } else if (e.key === KEY_ENTER && filteredSkills.length > 0) {
        e.preventDefault();
        handleSelect(filteredSkills[selectedIndex]);
      } else if (e.key === KEY_ESCAPE) {
        setDismissed(true);
        setTriggerState(null);
      }
    },
    [open, filteredSkills, selectedIndex, handleSelect],
  );

  const dismiss = useCallback(() => {
    setDismissed(true);
    setTriggerState(null);
  }, []);

  return {
    open,
    filteredSkills,
    selectedIndex,
    handleSelect,
    handleInputChange,
    handleKeyDown,
    dismiss,
  };
}
